import { memo, useCallback, useMemo } from 'react';
import { RackingArea } from '@thive/fddm-types';

import { GapHorizontalIcon } from '@/assets/icons';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { NumberInput } from '@/modules/common/components/inputs/components/NumberInput';
import { NumUtils } from '@/modules/common/utils';
import { getCommonNumber } from '@/modules/common/helpers/commonValue';
import {
  createEmptyAreas,
  pushAreaToItsCollection,
} from '@/modules/floorplanDesign/helpers/helpers';

interface Props {
  selectedRackingAreas: RackingArea[];
}

function OffsetDrivingComponent({ selectedRackingAreas }: Props) {
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const offsetDrivingMm = useMemo(
    () =>
      getCommonNumber(
        selectedRackingAreas.map((area) => NumUtils.metersToMmRounded(area.offsetDriving)),
      ),
    [selectedRackingAreas],
  );

  const onChange = useCallback(
    (newValueMm: number) => {
      if (Number.isNaN(newValueMm)) return;
      const newValue = NumUtils.mmToMeters(newValueMm);
      fddmSvc.setPreference('rackingArea.offsetDriving', newValue);

      const updatedAreas = createEmptyAreas();
      selectedRackingAreas.forEach((area) => {
        pushAreaToItsCollection(updatedAreas, { ...area, offsetDriving: newValue });
      });
      fddmSvc.updateAreas(updatedAreas);
    },
    [fddmSvc, selectedRackingAreas],
  );

  return (
    <PropertiesLabel i18nkey='interface:properties.area.rack.offset_driving'>
      <NumberInput
        value={offsetDrivingMm}
        unit='mm'
        prefix={<GapHorizontalIcon />}
        sx={{ width: '100%' }}
        minValue={0}
        onChange={onChange}
      />
    </PropertiesLabel>
  );
}

export const OffsetDriving = memo(OffsetDrivingComponent);
